<template>
  <v-data-table
    v-bind="$attrs"
    :headers="headers"
    :items="list"
    hide-default-footer
    disable-pagination
  >
    <template #item.log_type="{ value, item }">
      <div :class="getClassForName(item)" v-text="getName(value)" />
    </template>

    <template #item.history="{ item }">
      <div v-html="getHistory(item)" />
    </template>

    <template #item.status="{ value }">
      <BTooltipButton
        :icon="getIcon(value)"
        :tooltip="getTooltip(value)"
        top
      />
    </template>
  </v-data-table>
</template>

<script>
import moment from 'moment'
import BTooltipButton from '@/components/common/BTooltipButton.vue'
import { EventLogStatus, EventLogType } from '@/typings/api/Marks/EventLog'
import { dateFormatterIgnoreTz } from '@/helpers/dateFormatter'
import { MarksFilesApi } from '@/api/resources/Marks'

const HEADERS = [
  {
    text: '№',
    value: 'position',
    width: '32px',
    sortable: false,
  },
  {
    text: 'Задача',
    value: 'log_type',
    sortable: false,
  },
  {
    text: 'Статус',
    value: 'status',
    sortable: false,
    width: '64px',
  },
  {
    text: 'История',
    value: 'history',
    sortable: false,
  },
  {
    text: 'Комментарий',
    value: 'comment',
    sortable: false,
  },
]

export default {
  components: {
    BTooltipButton,
  },
  props: {
    list: {
      type: Array,
      required: true,
    },
  },
  data: () => ({
    headers: HEADERS,
  }),
  methods: {
    getClassForName({ status, user_action_history_date: date, position }) {
      let isNameWarning = false


      // Оранжевый цвет особенный для пунктов 6, 8 и 11
      if (position === 6) {
        // Цвет шрифта оранжевый, статус Выполнена,
        // если хотя бы в одной из задач №2 или №5 дата+время в истории задачи позже,
        // чем дата+время в истории задачи №6
        const subtasks = this.list.filter(({ position: p }) => [2, 5].includes(p))

        isNameWarning = status === EventLogStatus.DONE
          && subtasks.some((el) => moment(date).isBefore(moment(el.user_action_history_date)))
      } else if (position === 8) {
        /**
         * 1. Цвет шрифта оранжевый, статус Выполнена,
         * если дата+время в истории задачи №6 позже, чем дата+время в истории задачи №8
         */
        const task6 = this.list.find(({ position: p }) => p === 6)
        if (task6) {
          isNameWarning = status === EventLogStatus.DONE
          && moment(date).isBefore(moment(task6.user_action_history_date))
        }

        if (!task6 || !isNameWarning) {
          /**
           * 2. Цвет шрифта оранжевый, статус задачи Готова к выполнению, если статус задачи №7 НЕ равно Выполнена
           */
          const task7 = this.list.find(({ position: p }) => p === 7)
          if (task7) {
            isNameWarning = status === EventLogStatus.READY && task7.status !== EventLogStatus.DONE
          }
        }
      } else if (position === 11) {
        /**
         * 1. Цвет шрифта оранжевый, статус Выполнена, если дата+время в истории задачи №6 позже, чем дата+время в истории задачи №11
         */
        const task6 = this.list.find(({ position: p }) => p === 6)
        if (task6) {
          isNameWarning = status === EventLogStatus.DONE
          && moment(date).isBefore(moment(task6.user_action_history_date))
        }

        if (!task6 || !isNameWarning) {
          /**
           * 2. Цвет шрифта оранжевый, статус задачи Готова к выполнению, если статус задачи №7 НЕ равно Выполнена
           */
          const task7 = this.list.find(({ position: p }) => p === 7)
          if (task7) {
            isNameWarning = status === EventLogStatus.READY && task7.status !== EventLogStatus.DONE
          }

          if (!task7 || !isNameWarning) {
            /**
             * 3. Цвет шрифта оранжевый, статус задачи Готова к выполнению, если статус задачи №10 НЕ равно Выполнена
             */
            const task10 = this.list.find(({ position: p }) => p === 10)
            if (task10) {
              isNameWarning = status === EventLogStatus.READY && task10.status !== EventLogStatus.DONE
            }
          }
        }
      } else if (position === 3) {
        isNameWarning = false
      } else if (position > 1) {
        const prevValue = this.list.find(({ position: p }) => p === position - 1)
        isNameWarning = status === EventLogStatus.DONE
        && prevValue !== null && moment(date).isBefore(moment(prevValue.user_action_history_date))
      }


      return {
        NameWarning: isNameWarning,
        NameError: status === EventLogStatus.ERROR,
        NameUnavailable: status === EventLogStatus.UNAVAILABLE,
      }
    },
    getIcon(status) {
      switch (status) {
        case EventLogStatus.UNAVAILABLE:
          return 'mdi-radiobox-blank'
        case EventLogStatus.READY:
          return 'mdi-radiobox-marked'
        case EventLogStatus.IN_PROGRESS:
          return 'mdi-checkbox-blank-outline'
        case EventLogStatus.DONE:
          return 'mdi-checkbox-marked-outline'
        case EventLogStatus.ERROR:
          return 'mdi-minus-box-outline'
        default:
          return 'mdi-close-box-outline'
      }
    },
    getName(name) {
      switch (name) {
        case EventLogType.MARKS_UPLOAD:
          return 'Загрузка данных из системы Эксперт'
        case EventLogType.MARKS_SAVE:
          return 'Обработка данных из системы Эксперт'
        case EventLogType.OTHER_MARKS:
          return 'Наличие данных для дальнейшей работы (в т.ч. поступившие из внешних систем)'
        case EventLogType.QUESTIONS_UPLOAD:
          return 'Загрузка ключей'
        case EventLogType.QUESTIONS_SAVE:
          return 'Обработка ключей'
        case EventLogType.ANSWER_QUESTION_SYNC:
          return 'Синхронизация ключей и ответов учеников'
        case EventLogType.CALCULATE_MARKS:
          return 'Перевод ответов в баллы'
        case EventLogType.VEER_CREATION:
          return 'Формирование веера'
        case EventLogType.VEER_UPLOAD:
          return 'Загрузка обработанного веера'
        case EventLogType.VEER_SAVE:
          return 'Обработка веера'
        case EventLogType.FINAL_REPORT:
          return 'Формирование итоговой выгрузки'
        default:
          return name
      }
    },
    getTooltip(status) {
      switch (status) {
        case EventLogStatus.UNAVAILABLE:
          return 'Недоступна'
        case EventLogStatus.READY:
          return 'Готова к выполнению'
        case EventLogStatus.IN_PROGRESS:
          return 'В процессе'
        case EventLogStatus.DONE:
          return 'Выполнена'
        case EventLogStatus.ERROR:
          return 'Ошибка'
        default:
          return null
      }
    },
    getHistory({
      user_action_history_action,
      user_action_history_date,
      user_action_history_user,
      filename,
      file_id,
    }) {
      const result = []
      if (user_action_history_date) {
        result.push(dateFormatterIgnoreTz(user_action_history_date, 'DD.MM.YYYY HH:mm:ss'))
      }

      result.push(user_action_history_user)
      if (user_action_history_action) {
        result.push(this.getName(user_action_history_action))
      }
      if (filename) {
        const fApi = new MarksFilesApi()
        result.push(`
          <a 
          style="color: #2196f3; text-decoration: underline;" 
          href="${fApi.download(file_id)}" 
          download="${filename}">${filename}</a>`)
      }

      return result.filter(Boolean).join(' ')
    },
  },
}
</script>

<style lang="scss" scoped>
$color-gray: #878c97;

.NameUnavailable {
  color: $color-gray;
}

.NameWarning {
  color: $color-warning;
}

.NameError {
  color: $color-error;
}

.ViewLink {
  font-style: italic;
  color: $color-info;
  text-decoration: underline;
}
</style>
